import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { RecruiterContact, RecruiterSearch, RecruiterSearchKeys } from 'features/co-pilot/co-pilot.types';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import React from 'react';
import { useTranslation } from 'react-i18next';
import theme from 'theme';
import { makeStyles } from 'tss-react/mui';
import RecruiterContactsTable from './recruiter-contacts-table';
import RecruiterSearchConstraints from './recruiter-search-constraints';

interface Props {
  open: boolean;
  loading: boolean;
  preLoadingFields: boolean;
  contactsLoading: boolean;
  autoSyncLoading: boolean;

  constraints: RecruiterSearch;
  contacts: RecruiterContact[];

  totalContacts: number;
  pageNumber: number;

  handleChangeRecruiterSearchPage(event: unknown, newPage: number): void;

  handleRecruiterConstraintQueryChange(searchType: RecruiterSearchKeys): (event: React.ChangeEvent<HTMLInputElement>) => void;

  searchConstraintsKeyUp(searchType: RecruiterSearchKeys): () => void;

  searchConstraintsKeyDown(): void;

  handleSelectRecruiterConstraint(searchType: RecruiterSearchKeys): (value: string) => () => void;

  handleRemoveRecruiterConstraint(searchType: RecruiterSearchKeys, urn: string): () => void;

  handleExcludeChange(searchType: RecruiterSearchKeys, urn: string): (value: boolean) => () => void;

  handleKeywordsChange(event: React.ChangeEvent<HTMLInputElement>): void;

  keywordKeyDown(event: React.KeyboardEvent): void;

  handleAreaChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleTextFieldYearsOfExperienceChange(index: number): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleYearsOfExperienceChange(event: Event, newValue: number | number[], activeThumb: number): void;

  handleResetYearsOfExperience(): void;

  handleUpdateYearsOfExperience(): void;

  handleClose(): void;

  handleAcceptSearch(): void;
  showPostalCode?: boolean;
}

const RefineRecruiterSearchDialog: React.FC<Props> = ({
  open,
  loading,
  preLoadingFields,
  contactsLoading,
  autoSyncLoading,
  constraints,
  contacts,
  totalContacts,
  pageNumber,
  handleChangeRecruiterSearchPage,
  handleRecruiterConstraintQueryChange,
  searchConstraintsKeyUp,
  searchConstraintsKeyDown,
  handleSelectRecruiterConstraint,
  handleRemoveRecruiterConstraint,
  handleExcludeChange,
  handleKeywordsChange,
  keywordKeyDown,
  handleAreaChange,
  handleTextFieldYearsOfExperienceChange,
  handleYearsOfExperienceChange,
  handleResetYearsOfExperience,
  handleUpdateYearsOfExperience,
  handleClose,
  handleAcceptSearch,
  showPostalCode = true,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Dialog open={open} fullWidth={true} classes={{ paper: classes.paper }} maxWidth={preLoadingFields ? 'sm' : 'xl'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('recruiterSearchFineTuning')}</Typography>
      </DialogTitle>
      {preLoadingFields ? (
        <DialogContent className={classes.loadingContent}>
          <PaiDotLoader text={t('initializingFieldsMessage')} gap={1} textSize={15} />
        </DialogContent>
      ) : (
        <DialogContent className={classes.content}>
          <Stack direction={'row'} sx={{ width: '100%', height: '100%' }}>
            <div className={classes.contactsRow}>
              <RecruiterContactsTable
                contacts={contacts}
                contactsLoading={contactsLoading}
                totalContacts={totalContacts}
                pageNumber={pageNumber}
                handleChangeRecruiterSearchPage={handleChangeRecruiterSearchPage}
              />
            </div>
            <div className={classes.filterRow}>
              <RecruiterSearchConstraints
                constraints={constraints}
                autoSyncLoading={autoSyncLoading}
                loading={loading || contactsLoading}
                showPostalCode={showPostalCode}
                handleRecruiterConstraintQueryChange={handleRecruiterConstraintQueryChange}
                searchConstraintsKeyUp={searchConstraintsKeyUp}
                searchConstraintsKeyDown={searchConstraintsKeyDown}
                handleSelectRecruiterConstraint={handleSelectRecruiterConstraint}
                handleRemoveRecruiterConstraint={handleRemoveRecruiterConstraint}
                handleExcludeChange={handleExcludeChange}
                handleKeywordsChange={handleKeywordsChange}
                keywordKeyDown={keywordKeyDown}
                handleAreaChange={handleAreaChange}
                handleTextFieldYearsOfExperienceChange={handleTextFieldYearsOfExperienceChange}
                handleYearsOfExperienceChange={handleYearsOfExperienceChange}
                handleResetYearsOfExperience={handleResetYearsOfExperience}
                handleUpdateYearsOfExperience={handleUpdateYearsOfExperience}
              />
            </div>
          </Stack>
        </DialogContent>
      )}
      <DialogActions className={classes.actions}>
        <>
          <PaiLink text={t('cancel')} size={'md'} underline={'always'} action={handleClose} />
          <PaiButton text={t('save')} border={'rounded'} size={'small'} disabled={preLoadingFields || loading} action={handleAcceptSearch} />
        </>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()(() => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  loadingContent: {
    padding: '16px !important',
  },
  content: {
    padding: '0px !important',
    display: 'flex',
    whiteSpace: 'pre-wrap',
    height: '90vh',
    overflow: 'hidden',
  },
  contactsRow: {
    borderRight: `1px solid ${theme.palette.greys.lighter}`,
    width: 'calc(100% - 450px)',
    maxHeight: '100%',
    overflowY: 'auto',
  },
  filterRow: {
    padding: '16px',
    width: '450px',
    maxHeight: '100%',
    overflowY: 'auto',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '20px 16px 20px 16px',
    borderTop: `1px solid ${theme.palette.greys.lighter}`,
  },
}));

export default RefineRecruiterSearchDialog;
